import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import {
    FULL_MOTION,
    gsap,
    REDUCED_MOTION,
    useGSAP
} from '@/lib/gsap'

const HOLE = { x: 900, y: 70 }
const PUTT = `M 40 84 C 300 30, 560 118, ${HOLE.x} ${HOLE.y}`
const FULL_RADIUS = 1150

/**
 * The closing wordmark: a last putt rolls across the footer and drops at the
 * 19th hole, while the outlined MORROW fills with light under the pointer
 * (or, on touch screens, spreads out from the hole as you reach the bottom).
 */
export const FooterMark: FC = (): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const root = rootRef.current
        if (!root) return

        const svg = root.querySelector<SVGSVGElement>('svg')
        const light = root.querySelector<SVGCircleElement>('[data-mark-light]')
        const putt = root.querySelector<SVGPathElement>('[data-mark-putt]')
        const ball = root.querySelector<SVGCircleElement>('[data-mark-ball]')
        const flag = root.querySelector<SVGPolygonElement>('[data-mark-flag]')
        const contours = root.querySelectorAll<SVGEllipseElement>('[data-mark-contour]')
        if (!svg || !light || !putt || !ball || !flag) return

        const mm = gsap.matchMedia()

        mm.add(FULL_MOTION, () => {
            const length = putt.getTotalLength()
            const state = { progress: 0 }

            const placeBall = (): void => {
                const point = putt.getPointAtLength(length * state.progress)
                ball.setAttribute('cx', String(point.x))
                ball.setAttribute('cy', String(point.y))
            }

            placeBall()

            gsap.timeline({
                scrollTrigger: {
                    trigger: root,
                    start: 'top bottom',
                    end: 'bottom bottom',
                    scrub: 1
                }
            })
                .fromTo(contours, { strokeDashoffset: 1 }, { strokeDashoffset: 0, ease: 'none', stagger: 0.05, duration: 0.6 }, 0)
                .fromTo(putt, { strokeDashoffset: 1 }, { strokeDashoffset: 0, ease: 'none', duration: 0.85 }, 0)
                .to(state, { progress: 1, ease: 'power1.out', duration: 0.85, onUpdate: placeBall }, 0)
                .to(ball, { attr: { r: 0 }, ease: 'power2.in', duration: 0.1 }, 0.85)

            const wave = gsap.to(flag, {
                skewY: -8,
                scaleX: 0.9,
                transformOrigin: 'left center',
                duration: 1.6,
                ease: 'sine.inOut',
                repeat: -1,
                yoyo: true
            })

            return () => wave.kill()
        })

        // Fine pointers: a soft light follows the cursor and fills the letters beneath it.
        mm.add(`${FULL_MOTION} and (pointer: fine)`, () => {
            gsap.set(light, { attr: { r: 0, cx: HOLE.x, cy: HOLE.y } })

            const toSvg = (event: PointerEvent): { x: number, y: number } => {
                const rect = svg.getBoundingClientRect()
                const box = svg.viewBox.baseVal

                return {
                    x: ((event.clientX - rect.left) / rect.width) * box.width,
                    y: ((event.clientY - rect.top) / rect.height) * box.height
                }
            }

            const onMove = (event: PointerEvent): void => {
                const { x, y } = toSvg(event)
                gsap.to(light, { attr: { cx: x, cy: y }, duration: 0.7, ease: 'power3.out', overwrite: 'auto' })
            }

            const onEnter = (event: PointerEvent): void => {
                const { x, y } = toSvg(event)
                gsap.set(light, { attr: { cx: x, cy: y } })
                gsap.to(light, { attr: { r: 170 }, duration: 0.8, ease: 'expo.out' })
            }

            const onLeave = (): void => {
                gsap.to(light, { attr: { r: 0 }, duration: 0.6, ease: 'power3.in' })
            }

            root.addEventListener('pointermove', onMove)
            root.addEventListener('pointerenter', onEnter)
            root.addEventListener('pointerleave', onLeave)

            return () => {
                root.removeEventListener('pointermove', onMove)
                root.removeEventListener('pointerenter', onEnter)
                root.removeEventListener('pointerleave', onLeave)
            }
        })

        // Touch screens: the light spreads out from the hole as the footer settles.
        mm.add(`${FULL_MOTION} and (pointer: coarse)`, () => {
            gsap.fromTo(light, { attr: { r: 0, cx: HOLE.x, cy: HOLE.y } }, {
                attr: { r: FULL_RADIUS },
                ease: 'none',
                scrollTrigger: {
                    trigger: root,
                    start: 'top 85%',
                    end: 'bottom bottom',
                    scrub: 1
                }
            })
        })

        mm.add(REDUCED_MOTION, () => {
            gsap.set(light, { attr: { r: FULL_RADIUS } })
            gsap.set(ball, { attr: { r: 0 } })
            gsap.set([putt, ...contours], { strokeDashoffset: 0 })
        })

        return () => mm.revert()
    }, { scope: rootRef })

    return (
        <div ref={rootRef} className='relative select-none px-2 pt-6 sm:px-4'>
            <svg viewBox='0 0 1000 300' aria-hidden='true' className='block w-full overflow-visible'>
                <defs>
                    <linearGradient id='footer-mark-fill' x1='0' y1='0' x2='1' y2='1'>
                        <stop offset='0%' stopColor='var(--color-bone, #f2eee6)' />
                        <stop offset='55%' stopColor='var(--color-ochre, #c9a26b)' />
                        <stop offset='100%' stopColor='var(--color-clay, #a5683f)' />
                    </linearGradient>

                    <radialGradient id='footer-mark-light'>
                        <stop offset='0%' stopColor='#fff' />
                        <stop offset='70%' stopColor='#fff' />
                        <stop offset='100%' stopColor='#fff' stopOpacity='0' />
                    </radialGradient>

                    <mask id='footer-mark-mask' maskUnits='userSpaceOnUse' x='0' y='0' width='1000' height='300'>
                        <circle data-mark-light cx={HOLE.x} cy={HOLE.y} r='0' fill='url(#footer-mark-light)' />
                    </mask>
                </defs>

                {/* The green around the 19th hole, as survey contours. */}
                <g fill='none' stroke='currentColor' className='text-bone/[0.09]' strokeWidth='1'>
                    {[1, 2, 3, 4, 5, 6].map((step) => (
                        <ellipse
                            key={step}
                            data-mark-contour
                            cx={HOLE.x}
                            cy={HOLE.y}
                            rx={34 * step + step * step * 4}
                            ry={13 * step + step * 2}
                            transform={`rotate(-6 ${HOLE.x} ${HOLE.y})`}
                            pathLength={1}
                            strokeDasharray='1 1'
                        />
                    ))}
                </g>

                {/* The last putt. */}
                <path
                    data-mark-putt
                    d={PUTT}
                    fill='none'
                    stroke='currentColor'
                    className='text-bone/30'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    pathLength={1}
                    strokeDasharray='1 1'
                />

                <ellipse cx={HOLE.x} cy={HOLE.y} rx='9' ry='3.5' className='fill-[#0b0a08]' />
                <line x1={HOLE.x} y1={HOLE.y} x2={HOLE.x} y2='6' stroke='currentColor' className='text-bone/70' strokeWidth='1.5' />
                <polygon data-mark-flag points={`${HOLE.x},6 ${HOLE.x + 42},18 ${HOLE.x},30`} className='fill-ochre' />

                <text
                    x={HOLE.x - 16}
                    y={HOLE.y + 30}
                    textAnchor='end'
                    fill='currentColor'
                    className='fill-bone/45 font-mono uppercase'
                    style={{ fontSize: 11, letterSpacing: '0.24em' }}
                >
                    19th hole · The terrace
                </text>

                <circle data-mark-ball cx='40' cy='84' r='5.5' className='fill-bone' />

                {/* The wordmark: an outline, and the same letters in light beneath the mask. */}
                <text
                    x='500'
                    y='292'
                    textAnchor='middle'
                    textLength='984'
                    lengthAdjust='spacingAndGlyphs'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1'
                    className='type-wide font-sans font-semibold text-bone/25'
                    style={{ fontSize: 200 }}
                >
                    MORROW
                </text>

                <text
                    x='500'
                    y='292'
                    textAnchor='middle'
                    textLength='984'
                    lengthAdjust='spacingAndGlyphs'
                    fill='url(#footer-mark-fill)'
                    mask='url(#footer-mark-mask)'
                    className='type-wide font-sans font-semibold'
                    style={{ fontSize: 200 }}
                >
                    MORROW
                </text>
            </svg>
        </div>
    )
}
